/**
 * CreateTenantModal — Task 25.2 (DEC-113).
 *
 * Creates a hauler tenant: name, auto-derived (editable) slug, hauler abbreviation
 * (2–4 letters, drives the HaulId prefix — shown as a live preview), and optional
 * company fields. Slug/abbreviation uniqueness is enforced server-side; a 4xx from
 * the API surfaces as an inline error.
 */
import * as React from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { FormRow, fieldStyle, labelStyle, haulIdPreview, slugify } from '../../ui/bits';
import { useCreateTenant } from './hooks';
import { ABBREVIATION_RE, ABBREVIATION_RULE_TEXT } from '@lodex-pro/shared/abbreviation';

export interface CreateTenantModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onCreated: (id: string) => void;
}

export function CreateTenantModal({
  open,
  onOpenChange,
  onCreated,
}: CreateTenantModalProps): React.JSX.Element {
  const [name, setName] = React.useState('');
  const [slug, setSlug] = React.useState('');
  const [slugEdited, setSlugEdited] = React.useState(false);
  const [abbrev, setAbbrev] = React.useState('');
  const [dotNumber, setDotNumber] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [address, setAddress] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setName('');
    setSlug('');
    setSlugEdited(false);
    setAbbrev('');
    setDotNumber('');
    setEmail('');
    setPhone('');
    setAddress('');
    setError(null);
  }, [open]);

  // Auto-derive the slug from the name until the user edits it directly.
  const effectiveSlug = slugEdited ? slug : slugify(name);

  const createMutation = useCreateTenant((id) => {
    toast.success('Tenant created');
    onOpenChange(false);
    onCreated(id);
  });

  const handleSave = React.useCallback((): void => {
    if (name.trim() === '') {
      setError('Tenant name is required.');
      return;
    }
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(effectiveSlug)) {
      setError('Slug must be lowercase letters, numbers, and dashes.');
      return;
    }
    if (!ABBREVIATION_RE.test(abbrev.trim())) {
      setError(ABBREVIATION_RULE_TEXT);
      return;
    }
    setError(null);
    createMutation.mutate(
      {
        name: name.trim(),
        slug: effectiveSlug,
        haulerAbbreviation: abbrev.trim().toUpperCase(),
        ...(dotNumber.trim() !== '' && { dotNumber: dotNumber.trim() }),
        ...(email.trim() !== '' && { primaryEmail: email.trim() }),
        ...(phone.trim() !== '' && { primaryPhone: phone.trim() }),
        ...(address.trim() !== '' && { businessAddress: address.trim() }),
      },
      {
        onError: () =>
          setError('Could not create tenant — the slug or abbreviation may already be taken.'),
      },
    );
  }, [name, effectiveSlug, abbrev, dotNumber, email, phone, address, createMutation]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New hauler tenant</DialogTitle>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <FormRow label="Tenant name" htmlFor="t-name" required>
            <input
              id="t-name"
              value={name}
              onChange={(e): void => setName(e.target.value)}
              placeholder="e.g. Rivers Hauling LLC"
              style={fieldStyle}
            />
          </FormRow>
          <FormRow label="Slug" htmlFor="t-slug" hint="Used in URLs. Auto-filled from the name.">
            <input
              className="keep-case"
              id="t-slug"
              value={effectiveSlug}
              onChange={(e): void => {
                setSlugEdited(true);
                setSlug(e.target.value);
              }}
              placeholder="rivers-hauling"
              style={fieldStyle}
            />
          </FormRow>
          <FormRow
            label="Hauler abbreviation (letters only, 2–4)"
            htmlFor="t-abbrev"
            required
            hint={`Ticket IDs will look like: ${haulIdPreview(abbrev)}`}
          >
            <input
              id="t-abbrev"
              value={abbrev}
              maxLength={4}
              onChange={(e): void => setAbbrev(e.target.value.toUpperCase())}
              placeholder="RIV"
              style={{ ...fieldStyle, textTransform: 'uppercase', maxWidth: '140px' }}
            />
          </FormRow>
          <FormRow label="DOT number" htmlFor="t-dot">
            <input
              id="t-dot"
              value={dotNumber}
              onChange={(e): void => setDotNumber(e.target.value)}
              style={fieldStyle}
            />
          </FormRow>
          <FormRow
            label="Primary email"
            htmlFor="t-email"
            hint="Used to invite the tenant admin (provisioning coming soon)."
          >
            <input
              id="t-email"
              type="email"
              value={email}
              onChange={(e): void => setEmail(e.target.value)}
              style={fieldStyle}
            />
          </FormRow>
          <FormRow label="Primary phone" htmlFor="t-phone">
            <input
              id="t-phone"
              value={phone}
              onChange={(e): void => setPhone(e.target.value)}
              placeholder="+1 580 555 0100"
              style={fieldStyle}
            />
          </FormRow>
          <FormRow label="Business address" htmlFor="t-address">
            <input
              id="t-address"
              value={address}
              onChange={(e): void => setAddress(e.target.value)}
              style={fieldStyle}
            />
          </FormRow>
          {error !== null && (
            <p role="alert" style={{ ...labelStyle, color: 'var(--color-danger)' }}>
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={(): void => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={createMutation.isPending}
            onClick={handleSave}
            style={{
              backgroundColor: 'var(--color-accent-blue)',
              color: 'var(--color-on-accent)',
              borderColor: 'var(--color-accent-blue)',
            }}
          >
            {createMutation.isPending ? 'Creating…' : 'Create Tenant'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
